/**
 * Apps — the guided single-purpose workflows.
 *
 * <p>An app run owns a conversation, so everything about the transcript,
 * attachments and live streaming is the existing conversation API used with the
 * run's {@code conversationId}. This module covers only what is specific to a
 * run: the catalogue, the run's own state, and its versioned results.
 */

import client from './client';
import { APPS_API } from './routes';
import type { AskUserQuestion, MessageAttachment } from '@hooks/useChat';
import type { ApprovalMode } from '@utils/approvalMode';
import type { LocalRunner } from './localRunners';

export type AppOptionType =
  | 'SELECT'
  | 'MULTISELECT'
  | 'TOGGLE'
  | 'NUMBER'
  | 'SLIDER'
  | 'TEXT';

/** What an app needs before it can begin: files, or the user's first instruction. */
export type AppStartMode = 'FILES' | 'INSTRUCTION';

export type AppRunStatus =
  | 'DRAFT'
  | 'RUNNING'
  | 'WAITING_ON_USER'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

/** A string with a translation id and the English text to fall back on. */
export interface LocalizedText {
  readonly id: string;
  readonly default: string;
}

export interface AppOptionChoice {
  readonly value: string;
  readonly label: LocalizedText;
}

export interface AppOptionField {
  readonly id: string;
  readonly type: AppOptionType;
  readonly label: LocalizedText;
  readonly help?: LocalizedText | null;
  readonly default: unknown;
  readonly choices: readonly AppOptionChoice[];
  readonly min?: number | null;
  readonly max?: number | null;
  readonly step?: number | null;
  readonly maxLength?: number | null;
  /** Shown after a number's value, e.g. `mm`. */
  readonly unit?: string | null;
}

export interface AppInputSpec {
  readonly minFiles: number;
  readonly maxFiles: number;
  readonly acceptedExtensions: readonly string[];
}

export interface AppStepSpec {
  readonly id: string;
  readonly label: LocalizedText;
}

export interface AppDeliverableSpec {
  readonly extensions: readonly string[];
  readonly artifactTypes: readonly string[];
}

export interface AppExampleFile {
  readonly url: string;
  readonly filename: string;
  readonly contentType: string;
  /** A renderable stand-in, for an example the browser cannot draw itself. */
  readonly previewUrl?: string | null;
}

/** A worked example, shown in place of the empty stages before anything is uploaded. */
export interface AppExampleSpec {
  readonly inputs: readonly AppExampleFile[];
  readonly result?: AppExampleFile | null;
  readonly options?: Readonly<Record<string, unknown>> | null;
  /** The first instruction, for an app that starts on one. */
  readonly prompt?: string | null;
}

/** How an app appears outside its own page; the page is the rest. */
export interface AppPresentationSpec {
  /** Resolved against a fixed set of icons; anything unknown falls back. */
  readonly icon?: string | null;
  readonly example?: AppExampleSpec | null;
  /** The app's whole card in the gallery; the icon and text card stand in without it. */
  readonly cover?: string | null;
  /** Light-theme variant of {@link cover}; the dark one is shown when absent. */
  readonly coverLight?: string | null;
  /** A user app's shared cover; the server has already resolved it into {@link cover}. */
  readonly coverPreset?: string | null;
  /** The app's whole screen (`asset:<id>`); its bytes come from the page endpoints. */
  readonly page?: string | null;
  /** The App Kit major the page was written against; absent means the first. */
  readonly kitVersion?: number | null;
  readonly logo?: string | null;
}

/**
 * What the browser sees of an app. The run screen renders from this alone, which
 * is what lets a new app ship as a JSON file rather than a new page. The prompt,
 * skill allowlist and model pin stay on the server.
 */
export interface AppManifest {
  readonly key: string;
  readonly version: number;
  readonly name: LocalizedText;
  readonly tagline: LocalizedText;
  readonly inputs: AppInputSpec;
  /** Absent on runs frozen before apps could start on an instruction. */
  readonly start?: AppStartMode | null;
  readonly options: readonly AppOptionField[];
  /** Absent or empty for an app that never runs the agent. */
  readonly steps?: readonly AppStepSpec[] | null;
  /** Absent for an app that never runs the agent. */
  readonly deliverable?: AppDeliverableSpec | null;
  readonly expectedDurationNote?: LocalizedText | null;
  /** Absent on runs frozen before an app declared any. */
  readonly presentation?: AppPresentationSpec | null;
  /** Absent on manifests from before apps could run locally, which run in the cloud. */
  readonly runsOn?: AppRunsOn;
  /** Only `false` means the page never starts a run; absent means it uses the agent. */
  readonly usesAgent?: boolean | null;
}

/** Where an app's runs execute: the cloud, the person's own runner, or their pick of the two. */
export type AppRunsOn = 'CLOUD' | 'LOCAL' | 'CHOICE';

export type AppSource = 'BUILT_IN' | 'USER';

/** Who can run a user app: its creator alone, or everyone in the organization. */
export type AppVisibility = 'PRIVATE' | 'ORG';

export interface AppCatalogEntry {
  readonly appKey: string;
  readonly version: number;
  readonly manifest: AppManifest;
  /** Absent means built-in. */
  readonly source?: AppSource;
  readonly visibility?: AppVisibility | null;
  readonly createdByName?: string | null;
  readonly ownedByMe?: boolean;
  /** The caller holds this app only because they opened its public link. */
  readonly viaLink?: boolean;
  /** The app has a live public link; only ever set for the caller's own organization's apps. */
  readonly publicLink?: boolean;
  /** Another organization's app: its page and prompt are a stranger's. */
  readonly crossOrg?: boolean;
}

export interface AppArtifactDto {
  readonly artifactId: string;
  readonly conversationId: string;
  readonly name: string;
  readonly type: string;
  readonly contentType: string;
  readonly fileSize: number;
  readonly stepId?: string | null;
  readonly downloadUrl?: string | null;
  readonly createdAt: string;
}

export interface AppRunResultDto {
  readonly resultId: string;
  readonly version: number;
  /** Everything the turn behind this version delivered, in the order it did. */
  readonly artifacts: readonly AppArtifactDto[];
  /**
   * A browser-renderable stand-in for a deliverable the browser cannot draw —
   * the STL derived beside an exported CAD file. Null when the deliverable
   * needs none (an image) or none was produced.
   */
  readonly previewUrl?: string | null;
  /** The stand-in's filename; its extension is how the viewer picks a loader. */
  readonly previewName?: string | null;
  /** What the user asked for on the turn that produced this version. */
  readonly requestText?: string | null;
  /** What the app said about this version, read off the turn that produced it. */
  readonly notesText?: string | null;
  readonly createdAt: string;
}

/**
 * One exchange of a run: what the user asked and what the app said back.
 * `resultId` names the version the turn produced, null when the app answered
 * in text without changing the result.
 */
export interface AppRunTurnDto {
  readonly turnId: string;
  readonly requestText?: string | null;
  readonly replyText?: string | null;
  readonly resultId?: string | null;
  readonly createdAt: string;
}

export interface AppRunDetail {
  readonly runId: string;
  readonly appKey: string;
  readonly source?: AppSource;
  readonly conversationId: string;
  /** The run's own name, which is its conversation's title. */
  readonly title?: string | null;
  readonly status: AppRunStatus;
  readonly manifest: AppManifest;
  readonly currentStepId?: string | null;
  readonly options: Readonly<Record<string, unknown>>;
  readonly inputs: readonly MessageAttachment[];
  readonly results: readonly AppRunResultDto[];
  /** Every finished exchange, oldest first. */
  readonly turns: readonly AppRunTurnDto[];
  /** The request of the turn in flight, which no turn records yet. */
  readonly pendingRequestText?: string | null;
  /** Non-empty exactly when the run is waiting for these to be answered. */
  readonly pendingQuestions: readonly AskUserQuestion[];
  readonly failureStepId?: string | null;
  readonly failureReason?: string | null;
  /** True when the caller may view but not act — an org colleague's run. */
  readonly readOnly: boolean;
  /** A run of its creator's unpublished draft, kept out of the history. */
  readonly isTest?: boolean;
  /** Another organization's app, opened through its public link. */
  readonly crossOrg?: boolean;
  /** Its owner can make this test run the app's example. */
  readonly canPromoteToExample?: boolean;
  /** The owner's local runner the run executes on; absent on the cloud. */
  readonly runner?: string | null;
  readonly createdAt: string;
  readonly startedAt?: string | null;
  /** Start of the turn in flight, or of the last one to have run. */
  readonly turnStartedAt?: string | null;
  readonly finishedAt?: string | null;
}

/**
 * The option values the run starts with. Anything omitted falls back to the
 * manifest default server-side, so `{}` is a valid start.
 */
export interface StartAppRunRequest {
  readonly options: Readonly<Record<string, unknown>>;
  /** Anything the user wanted to say alongside the inputs. */
  readonly prompt?: string;
  /** The staged files to run on; omitted means every file staged on the run. */
  readonly attachmentIds?: readonly string[];
  /** 'cloud' or one of the caller's local runner ids; omitted means the cloud. */
  readonly runner?: string;
  readonly approvalMode?: ApprovalMode;
  /** Which of the app's alternative skills to use; omitted means the app's default. */
  readonly skill?: string;
}

export interface AppRunExecution {
  readonly runner: string;
  readonly approvalMode: ApprovalMode;
  readonly skill?: string;
}

export interface AppSkillRef {
  readonly id: string;
  readonly name: string;
}

export type AppRunnerProblem = 'OFFLINE' | 'OUTDATED' | 'MISSING_SOFTWARE';

export interface AppRunnerReadiness {
  readonly vmId: string;
  readonly name: string;
  readonly status: LocalRunner['status'];
  /** Absent when the runner can take the run. */
  readonly problem?: AppRunnerProblem | null;
  readonly missing: readonly AppSkillRef[];
  readonly unavailableAlternatives: readonly string[];
}

export interface AppRunSetup {
  readonly runsOn: AppRunsOn;
  /** Why the app cannot run anywhere right now; absent when it can. */
  readonly skillProblem?: string | null;
  /** The skills a local run checks: the software the app needs. */
  readonly required: readonly AppSkillRef[];
  readonly alternatives: readonly AppSkillRef[];
  readonly runners: readonly AppRunnerReadiness[];
}

/** One user mark over a result, in fractions of the previewed image. */
export interface AppRunMark {
  readonly kind: 'box' | 'freehand';
  readonly points: readonly (readonly [number, number])[];
}

/**
 * The user's next turn on a run that is not working: a correction of the latest
 * result, or the answers to the agent's questions.
 */
export interface RefineAppRunRequest {
  readonly text?: string;
  readonly marks?: readonly AppRunMark[];
  /** Attachment id of the composited snapshot carrying the marks. */
  readonly annotatedAttachmentId?: string;
  readonly questionAnswers?: readonly Record<string, unknown>[];
  /**
   * Crops of the areas drawn on while answering, which the folded answer legend
   * refers to by filename — so they have to travel on the answering turn.
   */
  readonly answerAttachmentIds?: readonly string[];
  /** What the user was looking at, shown to the agent for this turn only. */
  readonly viewSnapshot?: ViewSnapshot;
}

/** A screenshot of the app, sent with a turn and never stored. */
export interface ViewSnapshot {
  readonly mediaType: 'image/jpeg' | 'image/png' | 'image/webp';
  readonly base64: string;
}

/** What an app's HTML frame may load and how much the browser hands it. */
export interface AppHtmlPolicy {
  readonly allowedHosts: readonly string[];
  readonly maxPageBytes: number;
  readonly maxFeedFileBytes: number;
  readonly maxFeedTotalBytes: number;
}

export async function getAppHtmlPolicy(): Promise<AppHtmlPolicy> {
  const { data } = await client.get<AppHtmlPolicy>(`${APPS_API}/html-policy`);
  return data;
}

/** Opening a public link while signed in: from then on the app is listed and runs like one shared with you. */
export async function requestAppLinkAccess(token: string): Promise<{ appKey: string }> {
  const { data } = await client.post<{ appKey: string }>(`${APPS_API}/links/access`, { token });
  return data;
}

export async function listApps(): Promise<AppCatalogEntry[]> {
  const { data } = await client.get<AppCatalogEntry[]>(APPS_API);
  return data;
}

export async function createAppRun(appKey: string): Promise<AppRunDetail> {
  const { data } = await client.post<AppRunDetail>(
    `${APPS_API}/${encodeURIComponent(appKey)}/runs`,
  );
  return data;
}

/**
 * Start a fresh run from an existing one, settings carried over along with the
 * inputs named in {@code keepAttachmentIds} — all of them when it is omitted.
 */
export async function rerunAppRun(
  runId: string,
  keepAttachmentIds?: readonly string[],
): Promise<AppRunDetail> {
  const { data } = await client.post<AppRunDetail>(
    `${APPS_API}/runs/${encodeURIComponent(runId)}/rerun`,
    keepAttachmentIds ? { keepAttachmentIds } : undefined,
  );
  return data;
}

/** Before a run of the app exists, or for the caller's run judged by the app as it is today. */
export async function getAppRunSetup(target: { appKey: string } | { runId: string }): Promise<AppRunSetup> {
  const path =
    'runId' in target
      ? `${APPS_API}/runs/${encodeURIComponent(target.runId)}/run-setup`
      : `${APPS_API}/${encodeURIComponent(target.appKey)}/run-setup`;
  const { data } = await client.get<AppRunSetup>(path);
  return data;
}

export async function getAppRun(runId: string): Promise<AppRunDetail> {
  const { data } = await client.get<AppRunDetail>(
    `${APPS_API}/runs/${encodeURIComponent(runId)}`,
  );
  return data;
}

export async function cancelAppRun(runId: string): Promise<void> {
  await client.post(`${APPS_API}/runs/${encodeURIComponent(runId)}/cancel`);
}

export async function deleteAppRun(runId: string): Promise<void> {
  await client.delete(`${APPS_API}/runs/${encodeURIComponent(runId)}`);
}

/** The URL the SSE reader posts to; starting a run streams like any turn. */
export function startAppRunUrl(runId: string): string {
  return `${APPS_API}/runs/${encodeURIComponent(runId)}/start`;
}

export function refineAppRunUrl(runId: string): string {
  return `${APPS_API}/runs/${encodeURIComponent(runId)}/refine`;
}
